// public/js/features/settings/settings-api-access.js
// Settings → "API access" tab (visible only when the tenant holds the `api`
// add-on). Tenant admins create/list/revoke native API keys. The plaintext key is
// shown exactly once, in a copy modal. See docs/api-access.md.
import { getCurrentUser, can, isAdminLevel } from '../../modules/rbac.js';
import { loadTenants } from '../../modules/tenants.js';
import { scopeTenantParam } from '../../modules/tenant-scope.js';
import { scopePromptHtml } from '../../ui/tenant-scope-widget.js';
import { listApiKeys, createApiKey, revokeApiKey } from './api-keys-api.js';
import { listWebhooks, createWebhook, updateWebhook, deleteWebhook, testWebhook } from './webhooks-api.js';
import { showConfirm } from '../../modules/ui/confirm.js';
import { helpIcon, initHelp } from '../../ui/help.js';

const EVENT_LABELS = {
  'position.updated': 'Position updated',
  'alert.triggered': 'Alert triggered',
  'geofence.crossed': 'Geofence crossed',
};

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const SCOPE_LABELS = {
  'read:assets': 'Read assets',
  'read:history': 'Read history',
  'manage:webhooks': 'Manage webhooks',
};

const HELP = {
  intro:
    '<b>API access</b><br>' +
    'Create keys so your own systems can read this account\'s asset data over HTTPS. Each key is ' +
    'scoped and tied to this tenant. Send it as the <code>x-api-key</code> header to ' +
    '<code>/api/v1/public/…</code>. The full key is shown <b>once</b> at creation — store it safely.' +
    '<br><br><b>Aviation:</b> a key with <i>Read assets</i> lets your maintenance system pull the live ' +
    'location and sensor state of every ground unit each morning.',
  scopes:
    '<b>Scopes</b><br>What a key may do. <i>Read assets</i> = the asset list + detail; <i>Read history</i> ' +
    '= position/sensor history; <i>Manage webhooks</i> = configure push endpoints. Grant only what the ' +
    'integration needs.',
};

let selectedTenantId = null;

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(navigator.language); } catch { return s; }
}

// Resolve which tenants this user may manage keys for. Superadmin / listTenants:
// the api-enabled tenants. Otherwise single-tenant mode (server uses their sole
// tenant; we pass no tenantId). Returns { tenants|null }.
async function resolveTenants() {
  try {
    const all = await loadTenants();
    const apiEnabled = (all || []).filter(t => t?.addons?.api?.enabled === true);
    return { tenants: apiEnabled.length ? apiEnabled : all };
  } catch {
    return { tenants: null };   // no listTenants permission → single-tenant mode
  }
}

export async function renderApiAccessSettings(container) {
  const groups = getCurrentUser().groups || [];
  const isSuper = isAdminLevel(groups);
  if (!isSuper && !can(groups, 'manageTenants')) {
    container.innerHTML = `<div class="alert alert-danger">Access denied.</div>`;
    return;
  }

  container.innerHTML = `<div class="text-muted py-3"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>`;

  const { tenants } = await resolveTenants();
  // Global tenant scope (hard-coupled): the sidebar selector is the ONLY
  // tenant choice — under "All tenants" this page just prompts for a scope.
  // NB: `tenants` is filtered to API-ENABLED tenants — a selected scope tenant
  // that simply lacks the add-on must say so, not re-prompt for a tenant.
  const scopeTid = scopeTenantParam();
  const scopeIsSingle = !!(scopeTid && scopeTid !== 'unassigned');
  const scopePinned = !!(tenants && scopeIsSingle && tenants.find(t => t.tenantId === scopeTid));
  if (tenants && scopeIsSingle && !scopePinned) {
    container.innerHTML = `
      <div class="alert alert-warning" style="max-width:640px;">
        <b>API access is not enabled for this tenant.</b><br>
        <span class="small">The selected tenant does not hold the API add-on. Pick a different tenant in the sidebar, or enable the add-on on the Tenants page.</span>
      </div>`;
    return;
  }
  if (tenants && tenants.length > 1 && !scopePinned) {
    container.innerHTML = scopePromptHtml('API keys and webhooks');
    return;
  }
  selectedTenantId = scopePinned ? scopeTid : (tenants?.[0]?.tenantId || null);
  const tenantId = tenants ? selectedTenantId : null;  // null → server uses sole tenant

  let data;
  try {
    data = await listApiKeys(tenantId);
  } catch (e) {
    container.innerHTML = `<div class="alert alert-warning">${esc(e.message || 'Failed to load API keys')}</div>`;
    return;
  }
  const keys = data.keys || [];
  const allScopes = data.scopes || ['read:assets', 'read:history', 'manage:webhooks'];
  const reload = () => renderApiAccessSettings(container);

  container.innerHTML = `
    <div class="card shadow-sm mb-4 border-primary">
      <div class="card-header bg-primary text-white d-flex align-items-center gap-2">
        <i class="fa-solid fa-key"></i>
        <span class="fw-semibold">API access</span>
        <span class="badge bg-light text-primary ms-auto">applies to this tenant only</span>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">Keys let your own systems read this account's asset data over the API${helpIcon('intro')}. Base URL: <code>${esc(location.origin)}/api/v1/public/</code></p>

        <div class="border rounded p-3 mb-3 bg-light-subtle">
          <div class="fw-semibold mb-2"><i class="fa-solid fa-plus me-2"></i>Create a key</div>
          <div class="row g-2 align-items-end">
            <div class="col-md-5">
              <label class="form-label small mb-1">Label</label>
              <input id="ak-label" class="form-control form-control-sm" placeholder="e.g. Maintenance system" maxlength="80">
            </div>
            <div class="col-md-5">
              <label class="form-label small mb-1">Scopes${helpIcon('scopes')}</label>
              <div class="d-flex flex-wrap gap-3">
                ${allScopes.map(s => `
                  <div class="form-check">
                    <input class="form-check-input ak-scope" type="checkbox" value="${s}" id="ak-sc-${s.replace(/[^a-z]/gi, '')}" ${s === 'read:assets' ? 'checked' : ''}>
                    <label class="form-check-label small" for="ak-sc-${s.replace(/[^a-z]/gi, '')}">${esc(SCOPE_LABELS[s] || s)}</label>
                  </div>`).join('')}
              </div>
            </div>
            <div class="col-md-2 text-end">
              <button id="ak-create" class="btn btn-primary btn-sm w-100"><i class="fa-solid fa-check me-1"></i>Create</button>
            </div>
          </div>
        </div>

        <div class="small fw-semibold text-secondary mb-2">Keys (${keys.length})</div>
        ${keys.length ? `
        <div class="table-responsive">
          <table class="table table-sm align-middle mb-0">
            <thead><tr class="text-muted small">
              <th>Label</th><th>Prefix</th><th>Scopes</th><th>Created</th><th>Last used</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              ${keys.map(k => `
                <tr class="${k.revokedAt ? 'text-muted' : ''}">
                  <td class="fw-medium">${esc(k.label || '—')}</td>
                  <td><code>${esc(k.keyPrefix || '')}…</code></td>
                  <td>${(k.scopes || []).map(s => `<span class="badge text-bg-light border me-1">${esc(SCOPE_LABELS[s] || s)}</span>`).join('') || '—'}</td>
                  <td class="small">${fmtDate(k.createdAt)}</td>
                  <td class="small">${fmtDate(k.lastUsedAt)}</td>
                  <td>${k.revokedAt
                    ? '<span class="badge text-bg-secondary">Revoked</span>'
                    : '<span class="badge text-bg-success">Active</span>'}</td>
                  <td class="text-end">${k.revokedAt ? '' : `<button class="btn btn-sm btn-outline-danger ak-revoke" data-id="${esc(k.keyId)}"><i class="fa-solid fa-ban me-1"></i>Revoke</button>`}</td>
                </tr>`).join('')}
            </tbody>
          </table>
        </div>` : '<div class="text-muted small">No keys yet. Create one above.</div>'}
      </div>
    </div>

    <div id="ak-webhooks"></div>`;

  initHelp(container, HELP);

  container.querySelector('#ak-tenant')?.addEventListener('change', (e) => {
    selectedTenantId = e.target.value;
    reload();
  });

  container.querySelector('#ak-create')?.addEventListener('click', async () => {
    const btn = container.querySelector('#ak-create');
    const label = container.querySelector('#ak-label').value.trim();
    const scopes = [...container.querySelectorAll('.ak-scope:checked')].map(c => c.value);
    if (!label) { window.showToast('Give the key a label', 'warning'); return; }
    if (!scopes.length) { window.showToast('Pick at least one scope', 'warning'); return; }
    btn.disabled = true;
    try {
      const { key } = await createApiKey({ label, scopes, tenantId });
      showSecretModal('Your new API key', key, 'This is the only time the full key is shown. Copy it now and store it securely — you cannot retrieve it later.');
      reload();
    } catch (e) {
      window.showToast(e.message || 'Failed to create key', 'danger');
      btn.disabled = false;
    }
  });

  container.querySelectorAll('.ak-revoke').forEach(btn => {
    btn.addEventListener('click', async () => {
      const ok = await showConfirm({
        title: 'Revoke API key?',
        body: 'Any system using this key will immediately lose access. This cannot be undone.',
        confirmText: 'Revoke', variant: 'danger',
      });
      if (!ok) return;
      try {
        await revokeApiKey(btn.dataset.id, tenantId);
        window.showToast('Key revoked', 'success');
        reload();
      } catch (e) {
        window.showToast(e.message || 'Failed to revoke', 'danger');
      }
    });
  });

  mountWebhooks(container.querySelector('#ak-webhooks'), tenantId);
}

// ── Webhooks card (outbound push endpoints) ─────────────────────────────────────
async function mountWebhooks(host, tenantId) {
  if (!host) return;
  host.innerHTML = `<div class="text-muted small py-2"><span class="spinner-border spinner-border-sm me-2"></span>Loading webhooks…</div>`;
  let data;
  try { data = await listWebhooks(tenantId); }
  catch (e) { host.innerHTML = `<div class="alert alert-warning small">${esc(e.message || 'Failed to load webhooks')}</div>`; return; }
  const hooks = data.webhooks || [];
  const events = data.events || ['position.updated', 'alert.triggered', 'geofence.crossed'];
  const reload = () => mountWebhooks(host, tenantId);

  host.innerHTML = `
    <div class="card shadow-sm mb-4 border-primary">
      <div class="card-header bg-primary text-white d-flex align-items-center gap-2">
        <i class="fa-solid fa-bolt"></i><span class="fw-semibold">Webhooks</span>
        <span class="badge bg-light text-primary ms-auto">outbound push</span>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">We POST a signed JSON event to your HTTPS endpoint when something happens. Each request carries an <code>x-superb-signature</code> (HMAC-SHA256 of the body with your secret). Delivery is best-effort (no retry) — backfill gaps via <code>?since</code>.</p>

        <div class="border rounded p-3 mb-3 bg-light-subtle">
          <div class="fw-semibold mb-2"><i class="fa-solid fa-plus me-2"></i>Add endpoint</div>
          <div class="row g-2 align-items-end">
            <div class="col-md-6">
              <label class="form-label small mb-1">Endpoint URL (https)</label>
              <input id="wh-url" class="form-control form-control-sm" placeholder="https://example.com/superb-hook">
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Events</label>
              <div class="d-flex flex-wrap gap-2">
                ${events.map(e => `
                  <div class="form-check">
                    <input class="form-check-input wh-event" type="checkbox" value="${e}" id="wh-ev-${e.replace(/[^a-z]/gi, '')}" checked>
                    <label class="form-check-label small" for="wh-ev-${e.replace(/[^a-z]/gi, '')}">${esc(EVENT_LABELS[e] || e)}</label>
                  </div>`).join('')}
              </div>
            </div>
            <div class="col-md-2 text-end">
              <button id="wh-create" class="btn btn-primary btn-sm w-100"><i class="fa-solid fa-check me-1"></i>Add</button>
            </div>
          </div>
        </div>

        <div class="small fw-semibold text-secondary mb-2">Endpoints (${hooks.length})</div>
        ${hooks.length ? hooks.map(w => `
          <div class="border rounded p-2 mb-2 d-flex align-items-center gap-2 flex-wrap ${w.enabled ? '' : 'bg-light text-muted'}">
            <code class="flex-grow-1 text-truncate" style="min-width:200px;">${esc(w.url)}</code>
            <span>${(w.events || []).map(e => `<span class="badge text-bg-light border">${esc(EVENT_LABELS[e] || e)}</span>`).join(' ')}</span>
            <div class="form-check form-switch m-0" title="Enabled">
              <input class="form-check-input wh-toggle" type="checkbox" role="switch" data-id="${esc(w.id)}" ${w.enabled ? 'checked' : ''}>
            </div>
            <button class="btn btn-sm btn-outline-secondary wh-test" data-id="${esc(w.id)}"><i class="fa-solid fa-paper-plane me-1"></i>Test</button>
            <button class="btn btn-sm btn-outline-secondary wh-rotate" data-id="${esc(w.id)}" title="Rotate signing secret"><i class="fa-solid fa-rotate"></i></button>
            <button class="btn btn-sm btn-outline-danger wh-del" data-id="${esc(w.id)}"><i class="fa-solid fa-trash"></i></button>
          </div>`).join('') : '<div class="text-muted small">No endpoints yet.</div>'}
      </div>
    </div>`;

  host.querySelector('#wh-create')?.addEventListener('click', async () => {
    const btn = host.querySelector('#wh-create');
    const url = host.querySelector('#wh-url').value.trim();
    const evs = [...host.querySelectorAll('.wh-event:checked')].map(c => c.value);
    if (!/^https:\/\//i.test(url)) { window.showToast('Enter a valid https:// URL', 'warning'); return; }
    if (!evs.length) { window.showToast('Pick at least one event', 'warning'); return; }
    btn.disabled = true;
    try {
      const { webhook } = await createWebhook({ url, events: evs, tenantId });
      if (webhook?.secret) showSecretModal('Webhook signing secret', webhook.secret, 'Use this secret to verify the x-superb-signature header. Shown once now (rotate later if lost).');
      reload();
    } catch (e) { window.showToast(e.message || 'Failed to add endpoint', 'danger'); btn.disabled = false; }
  });

  host.querySelectorAll('.wh-toggle').forEach(t => t.addEventListener('change', async () => {
    try { await updateWebhook(t.dataset.id, { enabled: t.checked }, tenantId); window.showToast('Saved', 'success'); reload(); }
    catch (e) { window.showToast(e.message || 'Failed', 'danger'); reload(); }
  }));

  host.querySelectorAll('.wh-test').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true;
    try {
      const { result } = await testWebhook(b.dataset.id, tenantId);
      if (result?.ok) window.showToast(`Test delivered (HTTP ${result.status})`, 'success');
      else window.showToast(`Test failed: ${result?.error || 'HTTP ' + result?.status}`, 'warning');
    } catch (e) { window.showToast(e.message || 'Test failed', 'danger'); }
    finally { b.disabled = false; }
  }));

  host.querySelectorAll('.wh-rotate').forEach(b => b.addEventListener('click', async () => {
    const ok = await showConfirm({ title: 'Rotate signing secret?', body: 'The old secret stops working immediately. Any receiver must be updated with the new one.', confirmText: 'Rotate', variant: 'warning' });
    if (!ok) return;
    try {
      const r = await updateWebhook(b.dataset.id, { rotateSecret: true }, tenantId);
      if (r?.secret) showSecretModal('New webhook signing secret', r.secret, 'Update your receiver with this secret. Shown once now.');
    } catch (e) { window.showToast(e.message || 'Failed to rotate', 'danger'); }
  }));

  host.querySelectorAll('.wh-del').forEach(b => b.addEventListener('click', async () => {
    const ok = await showConfirm({ title: 'Delete endpoint?', body: 'We will stop sending events to this URL.', confirmText: 'Delete', variant: 'danger' });
    if (!ok) return;
    try { await deleteWebhook(b.dataset.id, tenantId); window.showToast('Endpoint deleted', 'success'); reload(); }
    catch (e) { window.showToast(e.message || 'Failed to delete', 'danger'); }
  }));
}

// One-time reveal of a secret (API key or webhook secret) with a copy button.
function showSecretModal(title, secret, note) {
  const el = document.createElement('div');
  el.className = 'modal fade';
  el.tabIndex = -1;
  el.innerHTML = `
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="fa-solid fa-key me-2"></i>${esc(title)}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning small mb-3"><i class="fa-solid fa-triangle-exclamation me-1"></i>${esc(note || '')}</div>
          <div class="input-group">
            <input id="ak-plain" class="form-control font-monospace" value="${esc(secret)}" readonly>
            <button id="ak-copy" class="btn btn-primary"><i class="fa-solid fa-copy me-1"></i>Copy</button>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Done</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(el);
  const modal = bootstrap.Modal.getOrCreateInstance(el);
  el.addEventListener('hidden.bs.modal', () => el.remove());
  el.querySelector('#ak-copy').addEventListener('click', async () => {
    const input = el.querySelector('#ak-plain');
    try {
      await navigator.clipboard.writeText(input.value);
      window.showToast('Key copied to clipboard', 'success');
    } catch {
      input.select(); document.execCommand('copy');
      window.showToast('Key copied', 'success');
    }
  });
  modal.show();
  el.querySelector('#ak-plain').addEventListener('focus', (e) => e.target.select());
}
